// Dr.Rx™ Dynamic SEO, OpenGraph & JSON-LD Schema Engine
import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
    title: string;
    description: string;
    name?: string;
    type?: string;
    image?: string;
    noindex?: boolean; // Prevent indexing for private pages
    schema?: Record<string, any>; // Structured Data (JSON-LD)
    keywords?: string[]; // Search keywords
    url?: string; // Canonical URL path (e.g., "/drug-index")
}

const SEO: React.FC<SEOProps> = ({ 
    title, 
    description, 
    name = 'Dr.Rx', 
    type = 'website',
    image = 'https://drrx.app/favicon.svg',
    noindex = false,
    schema,
    keywords,
    url
}) => {
    const canonicalUrl = `https://www.drrx.app${url || ''}`;
    
    return (
        <Helmet>
            {/* Standard Metadata */}
            <title>{title}</title>
            <meta name='description' content={description} />
            {keywords && <meta name="keywords" content={keywords.join(', ')} />}
            {noindex && <meta name="robots" content="noindex, nofollow" />}
            <link rel="canonical" href={canonicalUrl} />
            
            {/* Open Graph / Facebook */}
            <meta property='og:type' content={type} />
            <meta property='og:title' content={title} />
            <meta property='og:description' content={description} />
            <meta property='og:image' content={image} />
            <meta property='og:url' content={canonicalUrl} />
            
            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:creator" content={name} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {/* Structured Data (JSON-LD) */}
            {schema && (
                <script type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;